import React, { useState } from 'react';
import {
  PiUser,
  PiArrowsClockwise,
  PiDotsThreeVertical,
  PiUserMinus,
  PiPaperPlaneTilt,
  PiXCircle,
} from 'react-icons/pi';
import { Text } from 'src/modules/shared/components/atoms/text';
import Tag from 'src/modules/shared/components/atoms/tag';
import {
  TagDropdown,
  type TagDropdownOption,
} from 'src/modules/shared/components/molecules/dropdowns/tag-dropdown';
import { Table } from 'src/modules/shared/components/molecules/table/table';
import { Avatar } from 'src/modules/shared/components/atoms/avatar';
import type { UserFiltersState } from 'src/modules/users/types/user-filters.types';
import { InlineTextInput } from 'src/modules/shared/components/molecules/inputs/inline/inline-text-input';
import { InlineSelectInput } from 'src/modules/shared/components/molecules/inputs/inline/inline-select-input';
import {
  useUpdateUser,
  useDeactivateUser,
  useReactivateUser,
  useResendOnboarding,
  useCancelOnboarding,
  type UpdateUserData,
} from 'src/modules/shared/hooks/api/admin/users.mutation';
import type { Team, User } from 'src/api/generated';
import {
  isUserActive,
  isUserCancelled,
  isUserPending,
} from 'src/modules/shared/utils/users/status.util';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { UserPermission } from 'src/modules/shared/types/permissions.type';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useAlert } from 'src/contexts/alert-context';
import { extractApiErrorMessage } from 'src/modules/shared/utils/api/error-handler.util';
import { isValidPhoneNumber } from 'src/modules/shared/utils/validation/phone.util';
import { ProfileChangeDialog } from '../molecules/profile-change-dialog';

interface UsersTableProps {
  users: User[];
  teams: Team[];
  filters: UserFiltersState;
  loading: boolean;
}

export const UsersTable: React.FC<UsersTableProps> = ({
  users,
  teams,
  filters,
  loading,
}) => {
  const { t } = useI18n();
  const { showAlert } = useAlert();
  const { user: currentUser } = useCurrentUser();
  const { updateUser, loading: updateUserLoading } = useUpdateUser();
  const { deactivateUser, loading: deactivateUserLoading } =
    useDeactivateUser();
  const { reactivateUser, loading: reactivateUserLoading } =
    useReactivateUser();
  const { resendOnboarding, loading: resendOnboardingLoading } =
    useResendOnboarding();
  const { cancelOnboarding, loading: cancelOnboardingLoading } =
    useCancelOnboarding();

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pendingProfileChange, setPendingProfileChange] = useState<{
    user: User;
    newProfile: string;
  } | null>(null);

  const { hasPermission: canUpdateUser } = useHasPermissions(
    UserPermission.Update
  );
  const { hasPermission: canManageUsers } = useHasPermissions(
    UserPermission.Manage
  );
  const { hasPermission: canChangePermissions } = useHasPermissions(
    UserPermission.ChangePermission
  );

  const actionLoading =
    updateUserLoading ||
    deactivateUserLoading ||
    reactivateUserLoading ||
    resendOnboardingLoading ||
    cancelOnboardingLoading;

  const getInitials = (firstName: string | null, lastName: string | null) => {
    const first = firstName?.charAt(0).toUpperCase() ?? '';
    const last = lastName?.charAt(0).toUpperCase() ?? '';
    const initials = `${first}${last}`;
    return initials === '' ? '?' : initials;
  };

  const handleUserAction = async (userId: number, action: string) => {
    if (action === 'deactivate') {
      await deactivateUser(userId);
    } else if (action === 'reactivate') {
      await reactivateUser(userId);
    } else if (action === 'resend-onboarding') {
      await resendOnboarding(userId);
    } else if (action === 'cancel-onboarding') {
      await cancelOnboarding(userId);
    }
  };

  const handleUserUpdate = async (userId: number, userData: UpdateUserData) => {
    if (!canUpdateUser) return;

    await updateUser({
      userId,
      userData,
    });
  };

  const openConfirmProfileChange = (user: User, newProfile: string) => {
    setPendingProfileChange({ user, newProfile });
    setIsConfirmOpen(true);
  };

  const closeConfirm = () => {
    setIsConfirmOpen(false);
    setPendingProfileChange(null);
  };

  const handleConfirmProfileChange = async () => {
    if (!pendingProfileChange) return;

    await handleUserUpdate(pendingProfileChange.user.id, {
      profile: pendingProfileChange.newProfile,
    });
    closeConfirm();
  };

  const canUpdate = (user: User) => {
    if (!canUpdateUser) return false;
    if (isUserPending(user)) return canManageUsers;

    const isEditingSelf = currentUser?.id === user.id;

    return isEditingSelf || canManageUsers;
  };

  const handleInlineFieldError = (error: Error) => {
    showAlert(extractApiErrorMessage(error), 'error');
  };

  const validateIndividualPhoneNumber = (value: string) => {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return '';
    }

    if (!isValidPhoneNumber(trimmedValue)) {
      throw new Error(t('users.table.phoneInvalid'));
    }

    return trimmedValue;
  };

  const getStatusTag = (user: User) => {
    if (isUserPending(user)) {
      return {
        content: t('users.status.pending'),
        variant: 'warning' as const,
      };
    }

    if (isUserCancelled(user)) {
      return {
        content: t('users.status.cancelled'),
        variant: 'neutral' as const,
      };
    }

    if (isUserActive(user)) {
      return { content: t('users.status.active'), variant: 'success' as const };
    }

    return { content: t('users.status.inactive'), variant: 'danger' as const };
  };

  const buildUserActionOption = (
    id: string,
    label: string,
    color: 'secondary' | 'danger' | 'success',
    icon: React.ReactNode
  ): TagDropdownOption => ({
    id,
    content: (
      <Text size="small" color={color}>
        {label}
      </Text>
    ),
    icon,
  });

  const getUserActionOptions = (user: User): TagDropdownOption[] => {
    if (isUserPending(user)) {
      return [
        buildUserActionOption(
          'resend-onboarding',
          t('users.actions.resendAccess'),
          'secondary',
          <PiPaperPlaneTilt className="w-4 h-4 text-primary-700" />
        ),
        buildUserActionOption(
          'cancel-onboarding',
          t('users.actions.cancelInvite'),
          'danger',
          <PiXCircle className="w-4 h-4 text-danger-700" />
        ),
      ];
    }

    if (isUserCancelled(user)) {
      return [
        buildUserActionOption(
          'resend-onboarding',
          t('users.actions.resendAccess'),
          'secondary',
          <PiPaperPlaneTilt className="w-4 h-4 text-primary-700" />
        ),
      ];
    }

    const isActive = isUserActive(user);

    return [
      buildUserActionOption(
        isActive ? 'deactivate' : 'reactivate',
        isActive
          ? t('users.actions.deactivate')
          : t('users.actions.reactivate'),
        isActive ? 'danger' : 'success',
        isActive ? (
          <PiUserMinus className="w-4 h-4 text-danger-700" />
        ) : (
          <PiArrowsClockwise className="w-4 h-4 text-success-700" />
        )
      ),
    ];
  };

  const profileOptions = [
    { label: t('users.profile.agent'), value: 'agent' },
    { label: t('users.profile.admin'), value: 'admin' },
  ];

  const teamSelectOptions = [
    { label: t('users.team.none'), value: '' },
    ...teams.map((team) => ({
      label: team.name ?? t('users.team.fallback', { id: team.id }),
      value: String(team.id),
    })),
  ];

  const columns = [
    {
      header: t('users.table.user'),
      accessor: 'user',
      render: (_: unknown, user: User) => (
        <div className="flex justify-between items-center gap-3">
          <Avatar size="sm" text={getInitials(user.firstName, user.lastName)} />

          <div className="flex flex-row gap-1 justify-end whitespace-nowrap ">
            <InlineTextInput
              label={t('users.table.firstName')}
              value={user.firstName ?? ''}
              onSave={async (value) => {
                await handleUserUpdate(user.id, {
                  firstName: value.trim(),
                });
              }}
              size="sm"
              hideLabel
              alignment="right"
              disableCopy
              disabled={!canUpdate(user)}
            />
            <InlineTextInput
              label={t('users.table.lastName')}
              value={user.lastName ?? ''}
              onSave={async (value) => {
                await handleUserUpdate(user.id, {
                  lastName: value.trim(),
                });
              }}
              size="sm"
              hideLabel
              alignment="right"
              disableCopy
              disabled={!canUpdate(user)}
            />
          </div>
        </div>
      ),
    },
    {
      header: t('users.table.email'),
      accessor: 'email',
      render: (_: unknown, user: User) => (
        <Text size="tiny" color="secondary" className="whitespace-nowrap">
          {user.email}
        </Text>
      ),
    },
    {
      header: t('users.table.status'),
      accessor: 'status',
      render: (_: unknown, user: User) => {
        const statusTag = getStatusTag(user);
        return (
          <Tag
            content={statusTag.content}
            variant={statusTag.variant}
            size="sm"
          />
        );
      },
    },
    {
      header: t('users.table.profile'),
      accessor: 'profile',
      render: (_: unknown, user: User) => (
        <div className="flex justify-end items-center gap-2">
          <InlineSelectInput
            label={t('users.table.profile')}
            value={user.profile ?? 'agent'}
            options={profileOptions}
            onSave={(value) => {
              openConfirmProfileChange(user, value);
              return Promise.resolve();
            }}
            size="xs"
            hideLabel
            disableCopy
            disabled={!canUpdate(user) || !canChangePermissions}
          />
        </div>
      ),
    },
    {
      header: t('users.table.team'),
      accessor: 'team',
      render: (_: unknown, user: User) => (
        <div className="flex justify-end items-center gap-2">
          <InlineSelectInput
            label={t('users.table.team')}
            value={user.teamId ? String(user.teamId) : ''}
            options={teamSelectOptions}
            onSave={async (value) =>
              handleUserUpdate(user.id, {
                teamId: value ? parseInt(value) : null,
              })
            }
            size="xs"
            hideLabel
            disableCopy
            disabled={!canManageUsers}
          />
        </div>
      ),
    },
    {
      header: t('users.table.phone'),
      accessor: 'phone',
      render: (_: unknown, user: User) => (
        <InlineTextInput
          label={t('users.table.phoneLabel')}
          value={user.individualPhoneNumber ?? ''}
          onSave={async (value) => {
            const normalizedPhoneNumber = validateIndividualPhoneNumber(value);

            await handleUserUpdate(user.id, {
              individualPhoneNumber:
                normalizedPhoneNumber === '' ? null : normalizedPhoneNumber,
            });
          }}
          onError={handleInlineFieldError}
          size="sm"
          hideLabel
          disabled={!canUpdate(user)}
        />
      ),
    },
    {
      header: '',
      accessor: 'actions',
      render: (_: unknown, user: User) => {
        if (!canManageUsers) return null;

        return (
          <TagDropdown
            content={
              <div className="flex items-center">
                <PiDotsThreeVertical className="w-4 h-4" />
              </div>
            }
            variant="primary"
            appearance="soft"
            size="2xs"
            options={getUserActionOptions(user)}
            onOptionSelect={(option) => {
              void handleUserAction(user.id, option.id.toString());
            }}
            disabled={actionLoading}
          />
        );
      },
    },
  ];

  if (!loading && users.length === 0) {
    return (
      <EmptyState
        title={t('users.empty.title')}
        description={
          filters.search ||
          (filters.status && filters.status !== 'all') ||
          (filters.teamId && filters.teamId !== 'all')
            ? t('users.empty.filteredDescription')
            : t('users.empty.defaultDescription')
        }
        icon={<PiUser className="text-primary-500 w-full h-full" />}
        className="h-64"
      />
    );
  }

  return (
    <>
      <Table
        columns={columns}
        data={users}
        keyExtractor={(u) => u.id.toString()}
        isLoading={loading}
        variant="neutral"
      />

      <ProfileChangeDialog
        isOpen={isConfirmOpen}
        onClose={closeConfirm}
        onConfirm={handleConfirmProfileChange}
        pendingChange={pendingProfileChange}
        currentUser={currentUser}
        isLoading={updateUserLoading}
      />
    </>
  );
};
